import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { useGetAdmissionRequestDetailQuery } from '@/api/member-management/admissionRequest';
import { useGetFamilyDetailQuery } from '@/api/family-management/familyApi';
import {
	buildLinkFieldRows,
	LinkFieldRow,
	LinkRecordValues,
} from './linkSyncFields';

/**
 * `toMember`: the family member application form updates the member record.
 * `toFamilyMember`: the member record updates the family member record.
 */
export type LinkDirection = 'toMember' | 'toFamilyMember';

interface Props {
	open: boolean;
	direction: LinkDirection;
	/** `user.id` (toMember) or `family_member.id` (toFamilyMember). */
	targetId?: string;
	targetFileNo?: string | null;
	/** Values that win (§5.1: the newly submitted side). */
	source: LinkRecordValues;
	/** `true` = Confirm; `false` = Back. */
	onClose: (confirmed: boolean) => void;
}

/** DMS-REQ-WEB-CHA-005 §五: lists the fields the link will update on the other record. */
export default function LinkConfirmDialog({
	open,
	direction,
	targetId,
	targetFileNo,
	source,
	onClose,
}: Props) {
	const { t } = useTranslation();
	const skip = !open || !targetId;
	const { data: member, isFetching: memberFetching } =
		useGetAdmissionRequestDetailQuery(targetId || '', {
			refetchOnMountOrArgChange: true,
			skip: skip || direction !== 'toMember',
		});
	const { data: familyMember, isFetching: familyMemberFetching } =
		useGetFamilyDetailQuery(targetId || '', {
			refetchOnMountOrArgChange: true,
			skip: skip || direction !== 'toFamilyMember',
		});
	const target = direction === 'toMember' ? member?.data : familyMember?.data;
	const loading = memberFetching || familyMemberFetching || !target;
	const rows = target ? buildLinkFieldRows(source, target) : [];
	const fileNo =
		targetFileNo ??
		(direction === 'toMember' ? target?.fileNo : target?.familyMemberFileNo);
	const targetHref =
		direction === 'toMember'
			? `/member-management/member-list/detail/${targetId}`
			: `/family-management/family-management-list/detail/${targetId}`;
	const changedCount = rows.filter((row) => row.changed).length;
	const changedCellClass = (params: { row: LinkFieldRow }) =>
		params.row.changed ? 'link-changed' : '';

	const columns: GridColDef<LinkFieldRow>[] = [
		{
			field: 'label',
			headerName: t('family.field'),
			flex: 12,
			cellClassName: changedCellClass,
			renderCell: (params) => t(params.row.label),
		},
		{
			field: 'current',
			headerName: t('identityLink.currentValue'),
			flex: 15,
			cellClassName: changedCellClass,
			renderCell: (params) => (
				<Typography
					variant='body2'
					sx={
						params.row.changed
							? { textDecoration: 'line-through', color: 'text.secondary' }
							: undefined
					}
				>
					{params.row.current || '-'}
				</Typography>
			),
		},
		{
			field: 'next',
			headerName: t('identityLink.newValue'),
			flex: 15,
			cellClassName: changedCellClass,
			renderCell: (params) =>
				params.row.cleared ? (
					<Typography variant='body2' color='error' fontWeight='bold'>
						{t('identityLink.willBeCleared')}
					</Typography>
				) : (
					<Typography
						variant='body2'
						sx={params.row.changed ? { fontWeight: 'bold' } : undefined}
					>
						{params.row.next || '-'}
					</Typography>
				),
		},
	];

	return (
		<Dialog open={open} onClose={() => onClose(false)} maxWidth='md' fullWidth>
			<DialogTitle variant='h5'>{t('identityLink.confirmTitle')}</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose(false)}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '16px' }}>
					<Trans
						i18nKey={
							direction === 'toMember'
								? 'identityLink.confirmToMemberText'
								: 'identityLink.confirmToFamilyMemberText'
						}
						values={{
							fileNo: fileNo || '-',
							name: [target?.nameEn, target?.nameZh].filter(Boolean).join(' '),
						}}
						components={{
							anchor: (
								<Link
									href={targetHref}
									target='_blank'
									rel='noopener noreferrer'
									color='#1BBED7'
								/>
							),
						}}
					/>
				</Typography>
				{loading ? (
					<Box display='flex' justifyContent='center' padding={3}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Typography
							variant='body2'
							fontWeight='bold'
							sx={{ marginBottom: '8px' }}
						>
							{changedCount
								? t('identityLink.changedSummary', {
										count: changedCount,
										total: rows.length,
									})
								: t('identityLink.noFieldChanged')}
						</Typography>
						<Box sx={{ '& .link-changed': { bgcolor: '#FFF8E1' } }}>
							<LocalDataGrid
								rows={rows}
								columns={columns}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Box>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={() => onClose(false)}>
					{t('button.back')}
				</Button>
				<Button
					sx={{ textTransform: 'none' }}
					variant='contained'
					disabled={loading}
					onClick={() => onClose(true)}
				>
					{t(
						direction === 'toMember'
							? 'identityLink.confirmAndComplete'
							: 'identityLink.confirmAndContinue'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
